import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftRight, Sparkles } from 'lucide-react';
import { PageCenterLayout } from '../components/UI/PageCenterLayout';
import { AmbientBlobs, type BlobConfig } from '../components/UI/AmbientBlobs';
import { ErrorPageLayout } from '../components/UI/ErrorPageLayout';
import { Button } from '../components/UI/Button';
import { useAuthStore } from '../store/authStore';
import { getPortalUrl, clearDevPortalOverride } from '../lib/portal';

const BLOBS: BlobConfig[] = [
  {
    gradient: 'radial-gradient(circle, rgba(239,68,68,0.12) 0%, transparent 70%)',
    position: { top: '20%', left: '20%' },
    size: 400,
    duration: 6,
  },
  {
    gradient: 'radial-gradient(circle, rgba(239,68,68,0.08) 0%, transparent 70%)',
    position: { bottom: '20%', right: '20%' },
    size: 350,
    duration: 7,
    delay: 1.5,
  },
];

interface WrongPortalPageProps {
  targetPortal: 'creator' | 'viewer';
}

export const WrongPortalPage: React.FC<WrongPortalPageProps> = ({ targetPortal }) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const isTargetCreator = targetPortal === 'creator';

  const title = isTargetCreator
    ? 'Creator Mode only'
    : 'Viewer Mode only';

  const description = isTargetCreator
    ? "This page is only available in Creator Mode. You're currently in Viewer Mode."
    : "This page is only available in Viewer Mode. You're currently in Creator Mode.";

  function handleSwitch() {
    if (targetPortal === 'viewer') {
      clearDevPortalOverride();
      window.location.href = getPortalUrl('viewer');
      return;
    }
    if (user?.betaApproved || user?.isOwner) {
      window.location.href = getPortalUrl('creator');
      return;
    }
    navigate('/become-a-creator');
  }

  const switchLabel = isTargetCreator
    ? (user?.betaApproved || user?.isOwner ? 'Switch to Creator Mode' : 'Become a Creator')
    : 'Switch to Viewer Mode';

  const SwitchIcon = isTargetCreator && !(user?.betaApproved || user?.isOwner)
    ? Sparkles
    : ArrowLeftRight;

  return (
    <PageCenterLayout>
      <AmbientBlobs blobs={BLOBS} />
      <ErrorPageLayout
        characterState="Onboarding"
        title={title}
        description={description}
        cta={
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={handleSwitch}>
              <SwitchIcon style={{ width: 15, height: 15 }} />
              {switchLabel}
            </Button>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Go back
            </Button>
          </div>
        }
      />
    </PageCenterLayout>
  );
};
